import { useState } from 'react';
import { articles } from '@/mocks/articles';

const ARTICLES_PER_PAGE = 6;

export default function ArticleGrid() {
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = Math.ceil(articles.length / ARTICLES_PER_PAGE);
  const start = (currentPage - 1) * ARTICLES_PER_PAGE;
  const paginated = articles.slice(start, start + ARTICLES_PER_PAGE);

  const goTo = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <section className="bg-[#f5f5f0] py-16 flex-1">
      <div className="max-w-screen-xl mx-auto px-4">

        {/* Header */}
        <div className="mb-12">
          <div className="text-xs font-black tracking-widest text-gray-400 mb-2 uppercase">Nos experts écrivent pour vous</div>
          <h1 className="text-5xl md:text-6xl font-black text-[#1a1a1a] leading-none mb-4">
            LE BLOG<br />CLEVYS !
          </h1>
          <p className="text-gray-500 max-w-xl text-sm leading-relaxed">
            Conseils, guides et astuces de nos artisans experts en serrurerie, vitrerie et plomberie à Paris depuis 1982.
          </p>
        </div>

        {/* Category filters */}
        <div className="flex flex-wrap gap-2 mb-10">
          {['Tous', 'Serrurerie', 'Vitrerie', 'Plomberie', 'Sécurité'].map((cat) => (
            <span
              key={cat}
              className="text-xs font-black px-4 py-2 rounded-full border-2 border-[#1a1a1a] bg-white text-[#1a1a1a] cursor-default"
            >
              {cat}
            </span>
          ))}
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {paginated.map((article) => (
            <a
              key={article.id}
              href={article.url}
              target="_blank"
              rel="nofollow"
              className="group bg-white border-2 border-[#1a1a1a] rounded-2xl overflow-hidden flex flex-col hover:shadow-none transition-all hover:-translate-y-1 cursor-pointer"
            >
              {/* Image */}
              <div className="w-full h-48 overflow-hidden">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>

              {/* Content */}
              <div className="p-5 flex flex-col flex-1 gap-3">
                <div className="flex items-center justify-between">
                  <span className={`text-[10px] font-black px-3 py-1 rounded-full ${article.categoryColor}`}>
                    {article.category}
                  </span>
                  <span className="text-[10px] text-gray-400 font-semibold">{article.readTime} de lecture</span>
                </div>

                <h2 className="font-black text-[#1a1a1a] text-base leading-snug group-hover:text-red-600 transition-colors">
                  {article.title}
                </h2>

                <p className="text-gray-500 text-xs leading-relaxed flex-1">
                  {article.excerpt}
                </p>

                <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                  <span className="text-[10px] text-gray-400">{article.date}</span>
                  <span className="text-[10px] font-black text-[#1a1a1a] group-hover:text-red-600 transition-colors flex items-center gap-1">
                    LIRE L'ARTICLE <i className="ri-arrow-right-line"></i>
                  </span>
                </div>
              </div>
            </a>
          ))}
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => goTo(currentPage - 1)}
            disabled={currentPage === 1}
            className="w-10 h-10 flex items-center justify-center border-2 border-[#1a1a1a] rounded-full font-black text-sm hover:bg-[#FFD600] transition-colors disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
          </button>

          {Array.from({ length: totalPages }).map((_, i) => {
            const page = i + 1;
            return (
              <button
                key={page}
                onClick={() => goTo(page)}
                className={`w-10 h-10 flex items-center justify-center border-2 border-[#1a1a1a] rounded-full font-black text-sm transition-colors cursor-pointer ${
                  currentPage === page ? 'bg-[#FFD600] text-[#1a1a1a]' : 'bg-white hover:bg-[#FFD600]'
                }`}
              >
                {page}
              </button>
            );
          })}

          <button
            onClick={() => goTo(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="w-10 h-10 flex items-center justify-center border-2 border-[#1a1a1a] rounded-full font-black text-sm hover:bg-[#FFD600] transition-colors disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer"
          >
            <i className="ri-arrow-right-line"></i>
          </button>
        </div>

        <p className="text-center text-xs text-gray-400 mt-4">
          Page {currentPage} sur {totalPages} — {articles.length} articles
        </p>
      </div>
    </section>
  );
}
